import { useState, useEffect, useCallback, useRef } from 'preact/hooks';
import { useStore, usePrimaryAddress, type View } from '../lib/store';
import { messages as msgApi, sent as sentApi, addresses as addrApi, proofs as proofsApi, labels as labelsApi, auth as authApi } from '../lib/api';
import { LAST_ACCOUNT_KEY } from '../lib/nostr';
import { MessageList } from './MessageList';
import { MessageReader } from './MessageReader';
import { Aliases } from './Aliases';
import { Settings } from './Settings';
import { Compose } from './Compose';

interface InboxProps {
  domain: string;
}

export function Inbox({ domain }: InboxProps) {
  const store = useStore();
  const {
    session,
    setSession,
    currentView,
    setCurrentView,
    setAddresses,
    setMessages,
    setSentMail,
    setProofs,
    setLabels,
    setComposeOpen,
    showToast,
  } = store;

  const primaryAddress = usePrimaryAddress();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [lastUpdated, setLastUpdated] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // ─── Data loaders ───

  const loadInbox = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const [msgRes, addrRes, labelsRes] = await Promise.all([
        msgApi.list(),
        addrApi.list(),
        labelsApi.list().catch(() => ({ labels: [] })),
      ]);
      setAddresses(addrRes.addresses || []);
      setMessages(msgRes.messages || []);
      setLabels(labelsRes.labels || []);

      void loadAddresses();

      const time = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      setLastUpdated('Updated ' + time);
    } catch {
      setError(true);
    }
    setLoading(false);
  }, [setAddresses, setMessages, setLabels]);

  const loadAddresses = useCallback(async () => {
    try {
      const [addrData, proofsData] = await Promise.all([
        addrApi.list(),
        proofsApi.list(),
      ]);
      setAddresses(addrData.addresses || []);
      setProofs(proofsData.proofs || []);
    } catch (e) {
      console.error('Failed to load addresses:', e);
    }
  }, [setAddresses, setProofs]);

  const loadSent = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const data = await sentApi.list();
      setSentMail(data.sent || []);
      const time = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      setLastUpdated('Updated ' + time);
    } catch {
      setError(true);
    }
    setLoading(false);
  }, [setSentMail]);

  // ─── Navigation ───

  const navigate = useCallback(
    (view: View, skipHash = false) => {
      setCurrentView(view);
      store.setCurrentMessageId(null);
      if (!skipHash) {
        const hash = view === 'inbox' ? '#inbox' : view === 'sent' ? '#sent' : view === 'aliases' ? '#aliases' : '#settings';
        if (location.hash !== hash) {
          history.pushState(null, '', hash);
        }
      }

      const isMailView = view === 'inbox' || view === 'sent';
      if (isMailView) {
        if (view === 'inbox') void loadInbox();
        else void loadSent();
      } else if (view === 'aliases') {
        void loadAddresses();
      }
    },
    [setCurrentView, store, loadInbox, loadSent, loadAddresses],
  );

  // ─── Hash routing ───

  useEffect(() => {
    const handleHashChange = () => {
      const hash = location.hash.slice(1);
      if (!hash) {
        navigate('inbox', true);
        return;
      }
      const parts = hash.split('/');
      const rawView = parts[0];
      if (['inbox', 'sent', 'aliases', 'settings'].includes(rawView)) {
        const view = rawView as View;
        navigate(view, true);
        if (parts[1] && (view === 'inbox' || view === 'sent')) {
          setTimeout(() => {
            store.setCurrentMessageId(parts[1]);
          }, 300);
        }
      } else if (rawView === 'compose') {
        setComposeOpen(true);
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    if (location.hash) handleHashChange();
    return () => window.removeEventListener('hashchange', handleHashChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Initial load ───

  useEffect(() => {
    void loadInbox();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Logout ───

  const handleLogout = useCallback(async () => {
    await authApi.logout();
    localStorage.removeItem(LAST_ACCOUNT_KEY);
    setSession(null);
    location.reload();
  }, [setSession]);

  // ─── Copy address ───

  const handleCopyAddress = useCallback(() => {
    if (!primaryAddress) {
      showToast('No address available', 'error');
      return;
    }
    const full = primaryAddress.localPart + '@' + primaryAddress.domain;
    navigator.clipboard.writeText(full).then(() => {
      showToast('Address copied: ' + full, 'success');
    });
  }, [primaryAddress, showToast]);

  // ─── Refresh ───

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      if (currentView === 'inbox') await loadInbox();
      else if (currentView === 'sent') await loadSent();
      const time = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      setLastUpdated('Updated ' + time);
    } finally {
      setRefreshing(false);
    }
  }, [currentView, loadInbox, loadSent]);

  // ─── Search ───

  const handleSearch = useCallback(
    (query: string) => {
      store.setSearchQuery(query);
    },
    [store],
  );

  // ─── Keyboard shortcuts ───

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (store.composeOpen) {
          setComposeOpen(false);
        }
        return;
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        return;
      }

      const tag = (e.target as HTMLElement)?.tagName?.toLowerCase() || '';
      const isTyping = tag === 'input' || tag === 'textarea' || (e.target as HTMLElement)?.isContentEditable;
      if (isTyping) return;
      if (store.composeOpen) return;

      switch (e.key) {
        case 'j':
        case 'ArrowDown':
          e.preventDefault();
          navigateKeyboard(1);
          break;
        case 'k':
        case 'ArrowUp':
          e.preventDefault();
          navigateKeyboard(-1);
          break;
        case 'Enter':
          if (!store.currentMessageId) {
            e.preventDefault();
            openSelectedMessage();
          }
          break;
        case 'c':
          e.preventDefault();
          setComposeOpen(true);
          break;
        case '/':
          e.preventDefault();
          searchInputRef.current?.focus();
          break;
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store.composeOpen, store.currentMessageId, store.messages, store.sentMail, currentView]);

  const keyboardIdx = useRef(-1);

  const navigateKeyboard = useCallback(
    (direction: number) => {
      const list = currentView === 'inbox' ? store.messages : store.sentMail;
      if (!list || list.length === 0) return;
      if (direction > 0) {
        keyboardIdx.current = Math.min(keyboardIdx.current + 1, list.length - 1);
      } else {
        keyboardIdx.current = Math.max(keyboardIdx.current - 1, 0);
      }
      if (keyboardIdx.current < 0) keyboardIdx.current = 0;

      const items = document.querySelectorAll('.msg-item');
      items.forEach((el) => el.classList.remove('keyboard-active'));
      if (items[keyboardIdx.current]) {
        items[keyboardIdx.current].classList.add('keyboard-active');
        items[keyboardIdx.current].scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    },
    [currentView, store.messages, store.sentMail],
  );

  const openSelectedMessage = useCallback(() => {
    const list = currentView === 'inbox' ? store.messages : store.sentMail;
    if (!list || list.length === 0) return;
    if (keyboardIdx.current < 0) keyboardIdx.current = 0;
    const msg = list[keyboardIdx.current];
    if (msg && msg.id) {
      store.setCurrentMessageId(msg.id);
    }
  }, [currentView, store]);

  const unreadCount = store.messages.filter((m) => !m.isRead).length;

  const isMailView = currentView === 'inbox' || currentView === 'sent';
  const fullAddress = primaryAddress
    ? primaryAddress.localPart + '@' + primaryAddress.domain
    : '';

  return (
    <>
      <header>
        <h1 data-testid="brand-header">
          <span>{domain}</span>
        </h1>
        <div class="search-wrap">
          <input
            ref={searchInputRef}
            type="search"
            class="search-input"
            placeholder="Search mail..."
            aria-label="Search messages"
            value={store.searchQuery}
            onInput={(e) => handleSearch((e.target as HTMLInputElement).value)}
            data-testid="search-input"
          />
        </div>
        {fullAddress && (
          <div
            class="addr-display"
            onClick={handleCopyAddress}
            title={'Click to copy: ' + fullAddress}
            tabindex={0}
            data-testid="addr-chip"
          >
            {fullAddress}
          </div>
        )}
        <button class="btn btn-sm" id="compose-btn" data-testid="compose-btn" onClick={() => setComposeOpen(true)}>
          Compose
        </button>
        <button class="btn btn-ghost btn-sm" id="logout-btn" data-testid="logout-btn" onClick={() => void handleLogout()}>
          Logout
        </button>
      </header>

      <div id="app-view" data-testid="app-view">
        {isMailView && (
          <div class="app-shell">
            <LeftRail
              currentView={currentView}
              unreadCount={unreadCount}
              onNavigate={navigate}
              onCompose={() => setComposeOpen(true)}
              onLogout={() => void handleLogout()}
            />

            <div class="list-pane" id="list-pane" role="region" aria-label="Message list" data-testid="message-list">
              <div class="list-header">
                <h2>{currentView === 'sent' ? 'Sent' : 'Inbox'}</h2>
                <div style="display:flex;align-items:center;">
                  {lastUpdated && (
                    <span class="list-updated">{lastUpdated}</span>
                  )}
                  <button
                    class={'list-refresh' + (refreshing ? ' loading' : '')}
                    onClick={() => void handleRefresh()}
                    aria-label="Refresh messages"
                    disabled={refreshing}
                    data-testid="refresh-btn"
                  >
                    {refreshing ? (
                      <>
                        <span class="refresh-spinner" />
                        Refreshing...
                      </>
                    ) : (
                      'Refresh'
                    )}
                  </button>
                </div>
              </div>
              <div class="sidebar">
                {currentView === 'inbox' ? (
                  <MessageList
                    loading={loading}
                    error={error}
                    onRetry={() => void loadInbox()}
                    onCopyAddress={handleCopyAddress}
                  />
                ) : (
                  <MessageList
                    loading={loading}
                    error={error}
                    onRetry={() => void loadSent()}
                    onCopyAddress={handleCopyAddress}
                  />
                )}
              </div>
            </div>

            <MessageReader />
          </div>
        )}

        {currentView === 'aliases' && (
          <>
            <LeftRail
              currentView={currentView}
              unreadCount={unreadCount}
              onNavigate={navigate}
              onCompose={() => setComposeOpen(true)}
              onLogout={() => void handleLogout()}
            />
            <Aliases domain={domain} />
          </>
        )}

        {currentView === 'settings' && (
          <>
            <LeftRail
              currentView={currentView}
              unreadCount={unreadCount}
              onNavigate={navigate}
              onCompose={() => setComposeOpen(true)}
              onLogout={() => void handleLogout()}
            />
            <Settings />
          </>
        )}
      </div>

      <MobileNav currentView={currentView} onNavigate={navigate} />

      <Compose domain={domain} />
    </>
  );
}

function LeftRail({
  currentView,
  unreadCount,
  onNavigate,
  onCompose,
  onLogout,
}: {
  currentView: View;
  unreadCount: number;
  onNavigate: (v: View) => void;
  onCompose: () => void;
  onLogout: () => void;
}) {
  const navItems: { view: View; label: string; count?: number }[] = [
    { view: 'inbox', label: 'Inbox', count: unreadCount },
    { view: 'sent', label: 'Sent' },
    { view: 'aliases', label: 'Aliases' },
    { view: 'settings', label: 'Settings' },
  ];

  return (
    <nav class="left-rail" aria-label="Main navigation">
      <button class="btn rail-compose" onClick={onCompose}>
        Compose
      </button>
      <div class="nav-items">
        {navItems.map((item) => (
          <button
            key={item.view}
            class={'nav-item' + (currentView === item.view ? ' active' : '')}
            onClick={() => onNavigate(item.view)}
            data-testid={'nav-' + item.view}
          >
            <span>{item.label}</span>
            {item.count != null && item.count > 0 && (
              <span class="nav-count">{item.count}</span>
            )}
          </button>
        ))}
      </div>
      <div class="rail-footer">
        <button class="nav-item" onClick={onLogout} style="width:100%">
          <span>Logout</span>
        </button>
      </div>
    </nav>
  );
}

function MobileNav({
  currentView,
  onNavigate,
}: {
  currentView: View;
  onNavigate: (v: View) => void;
}) {
  const views: View[] = ['inbox', 'sent', 'aliases', 'settings'];
  return (
    <nav class="mobile-nav" aria-label="Mobile navigation">
      <div class="mobile-nav-items">
        {views.map((v) => (
          <button
            key={v}
            class={'mobile-nav-item' + (currentView === v ? ' active' : '')}
            onClick={() => onNavigate(v)}
            data-testid={`mobile-nav-${v}`}
          >
            {v.charAt(0).toUpperCase() + v.slice(1)}
          </button>
        ))}
      </div>
    </nav>
  );
}
